import {noop} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import ExpandedSidepanelThrobber from 'nav/expanded/ExpandedSidepanelThrobber';
import {ColumnAccessorKey} from 'nav/expanded/ListingRendererConstants';
import {Sort, SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ExpandedScrollableTable from 'ui-library/scrollable/ExpandedScrollableTable';
import * as ScrollableTableConstants from 'ui-library/scrollable/ScrollableTableConstants';
import {
  computeStateForToggledRows,
  getColumnHeaderSortDirection,
} from 'ui-library/scrollable/ScrollableTableUtil';
import fuzzydate from 'util/fuzzydate';

import {BenchlingAppSidepanelQuery} from './__generated/BenchlingAppSidepanelQuery';

type BenchlingAppSidepanelBenchlingApp = BenchlingAppSidepanelQuery['benchlingApps'][number];

interface BenchlingAppsExpandedTableProps {
  isCompact: boolean;
  isLoading: boolean;
  benchlingApps: Array<BenchlingAppSidepanelBenchlingApp>;
  emptyMessage: string;
  onUpdateSelectedIds: (processIds: Array<string>) => void;
  selectedIds: Array<string>;
  sorts: Array<Sort>;
  onChangeSort: (sortKey: string) => void;
}

export default function BenchlingAppsExpandedTable(props: BenchlingAppsExpandedTableProps): JSX.Element {
  const handleToggleSelectApps = (
    toggledRows: Array<ScrollableTableConstants.ScrollableTableRow>,
    isSelected: boolean
  ): void => {
    const newSelectedIdsState = computeStateForToggledRows({
      state: {selectedItemIds: props.selectedIds},
      // eslint-disable-next-line no-restricted-syntax
      toggledIds: _.pluck(toggledRows, 'id'),
      isSelected,
      rowSelectionMode: ScrollableTableConstants.RowSelectionMode.MULTIPLE,
    });
    props.onUpdateSelectedIds(newSelectedIdsState.selectedItemIds);
  };

  const columns = [
    {
      accessorKey: ColumnAccessorKey.ID,
      header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
      flexGrow: 1,
      isSortable: true,
      sortDirection: getColumnHeaderSortDirection(props.sorts, SortKey.SORT_IDENTIFIER_KEY),
      sortKey: SortKey.SORT_IDENTIFIER_KEY,
      isFixed: true,
    },
    {
      accessorKey: ColumnAccessorKey.NAME,
      header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
      flexGrow: 1,
      isSortable: true,
      sortDirection: getColumnHeaderSortDirection(props.sorts, SortKey.SORT_NAME_KEY),
      sortKey: SortKey.SORT_NAME_KEY,
    },
    {
      header: 'Last Seen',
      accessorKey: ColumnAccessorKey.LAST_SEEN,
      flexGrow: 1,
      isCollapsible: true,
      isSortable: false,
    },
  ];

  if (props.isLoading) {
    return <ExpandedSidepanelThrobber />;
  }

  const rows = props.benchlingApps.map((app) => ({
    id: app.id,
    data: {
      id: app.id,
      name: app.name,
      [ColumnAccessorKey.LAST_SEEN]: app.lastSeen ? fuzzydate(app.lastSeen) : null,
    },
    icon: <BenchlingIcon icon={ModelTypeConstants.BENCHLING_APP} className="scrollableTable-cell-icon" />,
    extra: {benchlingApp: app},
    isSelected: props.selectedIds.includes(app.id),
    isFaded: false,
  }));

  return (
    <ExpandedScrollableTable
      canFilterColumns={true}
      canResizeColumns={true}
      rowSelectionMode={ScrollableTableConstants.RowSelectionMode.MULTIPLE}
      className="u-flex-grow"
      columns={columns}
      emptyTableText={props.emptyMessage}
      getRowContentHeight={() => ScrollableTableConstants.TABLE_ROW_BODY_HEIGHT}
      isCompact={props.isCompact}
      matchParentHeight={true}
      onChangeSort={props.onChangeSort}
      onRowClick={(row: ScrollableTableConstants.ScrollableTableRow) => {
        FileBrowserActions.openFile(row.id);
      }}
      onRowContextMenu={noop}
      onRowsSelect={handleToggleSelectApps}
      rows={rows}
      tableKey={ScrollableTableConstants.TABLE_KEYS.EXPANDED_BENCHLING_APP_TABLE}
      defaultHiddenColumnKeys={[]}
    />
  );
}
